import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Resoluciones";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "Fecha no disponible";

  const fecha = new Date(fechaStr);

  if (Number.isNaN(fecha.getTime())) return "Fecha no disponible";

  return fecha.toLocaleDateString("es-CO");
};

const ListarResolucion = ({ refresh }) => {
  const [resoluciones, setResoluciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloResoluciones = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || null
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return null;
    }
  };

  const moduloResoluciones = useMemo(() => getModuloResoluciones(), []);

  const puedeLeer =
    moduloResoluciones?.puedeLeer === true ||
    moduloResoluciones?.leer === true ||
    moduloResoluciones?.ver === true ||
    moduloResoluciones?.listar === true ||
    moduloResoluciones?.read === true ||
    moduloResoluciones?.view === true;

  const puedeCrear =
    moduloResoluciones?.puedeCrear === true ||
    moduloResoluciones?.crear === true ||
    moduloResoluciones?.create === true;

  const puedeActualizar =
    moduloResoluciones?.puedeActualizar === true ||
    moduloResoluciones?.actualizar === true ||
    moduloResoluciones?.editar === true ||
    moduloResoluciones?.update === true;

  const puedeEliminar =
    moduloResoluciones?.puedeEliminar === true ||
    moduloResoluciones?.eliminar === true ||
    moduloResoluciones?.delete === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const getData = useCallback(async () => {
    if (!id) return;

    if (!puedeLeer) {
      setResoluciones([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/resolucion/listbyenteid/${id}`,
        authConfig()
      );

      if (data.ok && Array.isArray(data.resolucionEntes)) {
        const resolucionesProcesadas = data.resolucionEntes.map(
          (resolucion) => ({
            ...resolucion,

            nombreEnteconformador:
              resolucion.enteObjectId?.nombreEnteconformador ||
              "Ente no disponible",

            coddaneEnteconformador:
              resolucion.enteObjectId?.coddaneEnteconformador ||
              "Dane no disponible",

            nitEnteconformador:
              resolucion.enteObjectId?.nitEnteconformador ||
              "Nit no disponible",

            fechaResolucion: formatearFecha(
              resolucion.fechaResolucionEnte
            ),
          })
        );

        setResoluciones(resolucionesProcesadas.reverse());
      } else {
        setResoluciones([]);
      }
    } catch (error) {
      console.error("Error al cargar las resoluciones:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudieron cargar las resoluciones.",
        "error"
      );

      setResoluciones([]);
    } finally {
      setLoading(false);
    }
  }, [id, puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  const deleteResolucion = useCallback(
    async (idResolucion) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar resoluciones.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(idResolucion);

        const { data } = await axios.delete(
          `/api/resolucion/${idResolucion}`,
          authConfig()
        );

        if (data.ok) {
          setResoluciones((prev) =>
            prev.filter((resolucion) => resolucion._id !== idResolucion)
          );

          Swal.fire({
            icon: "success",
            title: data.message || "Resolución eliminada",
            showConfirmButton: false,
            timer: 1500,
          });

          return true;
        }

        Swal.fire("Error", "No se pudo eliminar la resolución.", "error");
        return false;
      } catch (error) {
        console.error("Error al eliminar la resolución:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la resolución.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const dataTabla = useMemo(
    () =>
      resoluciones.map((resolucion) => ({
        ...resolucion,

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/enteconformador/resoluciones/editar/${resolucion._id}`
                  )
                }
                title="Editar Resolución"
                type="button"
                disabled={loading || deletingId === resolucion._id}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteResolucion(resolucion._id)}
                title="Eliminar Resolución"
                type="button"
                disabled={loading || deletingId === resolucion._id}
              >
                {deletingId === resolucion._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      resoluciones,
      puedeActualizar,
      puedeEliminar,
      loading,
      deletingId,
      navigate,
      deleteResolucion,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "Código", accessor: "codResolucionEnte" },
      { Header: "Fecha", accessor: "fechaResolucion" },
      { Header: "Tipo", accessor: "tipoResolucionEnte" },
      { Header: "Nit Ente", accessor: "nitEnteconformador" },
      { Header: "Dane Ente", accessor: "coddaneEnteconformador" },
      { Header: "Ente Conformador", accessor: "nombreEnteconformador" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de lectura
              </h5>

              <p className="mb-4">
                No tienes permiso para ver las resoluciones de este módulo.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Listado de resoluciones
                </h5>

                <small className="text-secondary">
                  Gestión de resoluciones asociadas al ente conformador.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(`/enteconformador/resoluciones/registrar/${id}`)
                    }
                    disabled={loading}
                    title="Registrar Resolución"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando resoluciones…</div>
              </div>
            ) : Array.isArray(dataTabla) && dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📄</div>

                <div className="fw-semibold">
                  No hay resoluciones registradas
                </div>

                <div className="small mb-3">
                  No se encontraron resoluciones para este ente.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(`/enteconformador/resoluciones/registrar/${id}`)
                    }
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar resolución
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarResolucion;